/**
 * Máquina de SUBIDA DE VÍDEO (XState v5), Diseño B / F2-B.
 *
 * Flujo multipart presignado directo navegador→R2, RESUMABLE:
 *   selecting → creating → uploading (bucle de partes) → completing → done
 *                     ↘ failed (RETRY reanuda desde las partes persistidas)
 *                     ↘ aborted (ABORT)
 *
 * La máquina es PURA: TODA llamada de red se inyecta como actor (`createUpload`,
 * `uploadParts`, `completeUpload`, `abortUpload`) y la persistencia a IndexedDB
 * como acción (`persist`). Así se testea con servicios mockeados sin red ni DB.
 *
 * Reanudación: `persist` guarda `{assetId, uploadId, uploadedParts}`; al reabrir,
 * se arranca con `input.resume` y `createUpload` en modo resume hace `list_parts`
 * para no re-subir lo ya subido.
 */

import { assign, fromPromise, setup } from "xstate";
import type { VideoAssetMeta } from "./types";

/** Placeholder inerte para el input de un actor si el archivo aún no está (no
 * ocurre en la práctica: el guard de START exige `file`). Evita casts inseguros. */
const EMPTY_FILE: UploadFile = { name: "", size: 0, type: "" };

/** Una parte multipart completada (número + ETag de R2/S3). */
export interface CompletedPart {
  partNumber: number;
  etag: string;
}

/** Metadatos del archivo a subir (el blob real lo maneja la capa de API). */
export interface UploadFile {
  name: string;
  size: number;
  type: string;
  /** Referencia opaca al blob/File (la usan los actores de API; la máquina no la toca). */
  blob?: unknown;
}

/** Estado de reanudación persistido en IndexedDB. */
export interface ResumeState {
  assetId: string;
  uploadId: string;
  uploadedParts: CompletedPart[];
}

/** Resultado de `createUpload` (o de su modo resume = list_parts). */
export interface CreateUploadResult {
  assetId: string;
  uploadId: string;
  storageKey: string;
  partSize: number;
  /** Partes ya subidas (vacío en creación nueva; poblado al reanudar). */
  uploadedParts: CompletedPart[];
  /** Nº total de partes esperadas. */
  totalParts: number;
}

export interface UploadContext {
  file: UploadFile | null;
  title: string;
  posterKey: string | null;
  durationSeconds: number | null;
  assetId: string | null;
  uploadId: string | null;
  storageKey: string | null;
  partSize: number;
  totalParts: number;
  uploadedParts: CompletedPart[];
  uploadedCount: number;
  asset: VideoAssetMeta | null;
  error: string | null;
  resume: ResumeState | null;
}

export type UploadEvent =
  | {
      type: "SELECT";
      file: UploadFile;
      title?: string;
      posterKey?: string;
      durationSeconds?: number;
    }
  | { type: "START" }
  | { type: "PROGRESS"; uploadedCount: number; uploadedParts: CompletedPart[] }
  | { type: "RETRY" }
  | { type: "ABORT" };

export interface UploadInput {
  file?: UploadFile | null;
  title?: string;
  resume?: ResumeState | null;
}

// ─── Inputs de los actores (tipados para inyección + test) ────────────────────

export interface CreateUploadInput {
  file: UploadFile;
  title: string;
  resume: ResumeState | null;
}
export interface UploadPartsInput {
  file: UploadFile;
  assetId: string;
  uploadId: string;
  storageKey: string;
  partSize: number;
  totalParts: number;
  uploadedParts: CompletedPart[];
}
export interface CompleteUploadInput {
  assetId: string;
  uploadId: string;
  storageKey: string;
  parts: CompletedPart[];
}
export interface AbortUploadInput {
  assetId: string;
  uploadId: string;
}

export const mediaUploadMachine = setup({
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion -- XState v5 type-inference pattern
  types: {} as {
    context: UploadContext;
    events: UploadEvent;
    input: UploadInput;
  },
  actors: {
    // Crea el multipart upload (o lo reanuda vía list_parts). Sobrescrito por la
    // capa de API / los tests.
    // oxlint-disable-next-line typescript/require-await
    createUpload: fromPromise(
      async ({ input }: { input: CreateUploadInput }): Promise<CreateUploadResult> => {
        throw new Error(`createUpload actor not provided (${input.file.name})`);
      },
    ),
    // Sube las partes que falten (bucle interno, presign + PUT). Devuelve TODAS
    // las partes completadas (las previas + las nuevas).
    // oxlint-disable-next-line typescript/require-await
    uploadParts: fromPromise(
      async ({ input }: { input: UploadPartsInput }): Promise<CompletedPart[]> => {
        throw new Error(`uploadParts actor not provided (${input.uploadId})`);
      },
    ),
    // Cierra el multipart y marca el asset listo (server-side).
    // oxlint-disable-next-line typescript/require-await
    completeUpload: fromPromise(
      async ({ input }: { input: CompleteUploadInput }): Promise<VideoAssetMeta> => {
        throw new Error(`completeUpload actor not provided (${input.assetId})`);
      },
    ),
    // Aborta el multipart (limpia partes subidas).
    // oxlint-disable-next-line typescript/require-await
    abortUpload: fromPromise(async ({ input }: { input: AbortUploadInput }): Promise<void> => {
      throw new Error(`abortUpload actor not provided (${input.assetId})`);
    }),
  },
  actions: {
    // Persiste {assetId, uploadId, uploadedParts} en IndexedDB. No-op por defecto
    // (inyéctese en la capa de UI). Debe ser puro respecto a la máquina.
    persist: () => {
      /* override en UI */
    },
    clearPersisted: () => {
      /* override en UI */
    },
  },
}).createMachine({
  id: "mediaUpload",
  context: ({ input }) => ({
    file: input.file ?? null,
    title: input.title ?? input.file?.name ?? "Vídeo",
    posterKey: null,
    durationSeconds: null,
    assetId: input.resume?.assetId ?? null,
    uploadId: input.resume?.uploadId ?? null,
    storageKey: null,
    partSize: 0,
    totalParts: 0,
    uploadedParts: input.resume?.uploadedParts ?? [],
    uploadedCount: input.resume?.uploadedParts.length ?? 0,
    asset: null,
    error: null,
    resume: input.resume ?? null,
  }),
  // Si arranca con file (o resume), pasa directo a selecting; START dispara.
  initial: "selecting",
  states: {
    selecting: {
      on: {
        SELECT: {
          actions: assign(({ event }) => ({
            file: event.file,
            title: event.title ?? event.file.name,
            posterKey: event.posterKey ?? null,
            durationSeconds: event.durationSeconds ?? null,
          })),
        },
        START: {
          target: "creating",
          guard: ({ context }) => context.file !== null,
        },
      },
    },

    creating: {
      invoke: {
        src: "createUpload",
        input: ({ context }): CreateUploadInput => ({
          // file no puede ser null aquí (guard de START).
          file: context.file ?? EMPTY_FILE,
          title: context.title,
          resume: context.resume,
        }),
        onDone: {
          target: "uploading",
          actions: [
            assign(({ event }) => ({
              assetId: event.output.assetId,
              uploadId: event.output.uploadId,
              storageKey: event.output.storageKey,
              partSize: event.output.partSize,
              totalParts: event.output.totalParts,
              uploadedParts: event.output.uploadedParts,
              uploadedCount: event.output.uploadedParts.length,
              error: null,
            })),
            "persist",
          ],
        },
        onError: {
          target: "failed",
          actions: assign(({ event }) => ({ error: errorMessage(event.error) })),
        },
      },
      on: { ABORT: "aborting" },
    },

    uploading: {
      on: {
        PROGRESS: {
          actions: [
            assign(({ event }) => ({
              uploadedCount: event.uploadedCount,
              uploadedParts: event.uploadedParts,
            })),
            "persist",
          ],
        },
        ABORT: "aborting",
      },
      invoke: {
        src: "uploadParts",
        input: ({ context }): UploadPartsInput => ({
          file: context.file ?? EMPTY_FILE,
          assetId: context.assetId ?? "",
          uploadId: context.uploadId ?? "",
          storageKey: context.storageKey ?? "",
          partSize: context.partSize,
          totalParts: context.totalParts,
          uploadedParts: context.uploadedParts,
        }),
        onDone: {
          target: "completing",
          actions: [
            assign(({ event }) => ({
              uploadedParts: event.output,
              uploadedCount: event.output.length,
              error: null,
            })),
            "persist",
          ],
        },
        onError: {
          target: "failed",
          actions: assign(({ event }) => ({ error: errorMessage(event.error) })),
        },
      },
    },

    completing: {
      invoke: {
        src: "completeUpload",
        input: ({ context }): CompleteUploadInput => ({
          assetId: context.assetId ?? "",
          uploadId: context.uploadId ?? "",
          storageKey: context.storageKey ?? "",
          parts: context.uploadedParts,
        }),
        onDone: {
          target: "done",
          actions: [
            assign(({ event }) => ({ asset: event.output, error: null })),
            "clearPersisted",
          ],
        },
        onError: {
          target: "failed",
          actions: assign(({ event }) => ({ error: errorMessage(event.error) })),
        },
      },
    },

    failed: {
      on: {
        // RETRY reanuda: vuelve a creating, que en modo resume hace list_parts.
        RETRY: {
          target: "creating",
          actions: assign(({ context }) => ({
            resume:
              context.assetId && context.uploadId
                ? {
                    assetId: context.assetId,
                    uploadId: context.uploadId,
                    uploadedParts: context.uploadedParts,
                  }
                : context.resume,
          })),
        },
        ABORT: "aborting",
      },
    },

    aborting: {
      invoke: {
        src: "abortUpload",
        input: ({ context }): AbortUploadInput => ({
          assetId: context.assetId ?? "",
          uploadId: context.uploadId ?? "",
        }),
        // Aborte OK o falle, terminamos en aborted (limpiando lo persistido).
        onDone: { target: "aborted", actions: "clearPersisted" },
        onError: { target: "aborted", actions: "clearPersisted" },
      },
    },

    done: { type: "final" },
    aborted: { type: "final" },
  },
});

/** Extrae un mensaje de error legible de un `unknown` (onError de XState). */
function errorMessage(err: unknown): string {
  if (err instanceof Error) {
    return err.message;
  }
  if (typeof err === "string") {
    return err;
  }
  return "upload: error desconocido";
}
